// Debug panel component for real-time algorithm monitoring
import { useState, useEffect, useRef, useId } from 'react';
import { apiClient } from '../services/api';

type DebugLog = {
  id: string;
  timestamp: string;
  message: string;
  data?: any;
};

type AlgorithmState = {
  buckets: {
    expired: number;
    new: number;
    waiting: number;
    mastered: number;
    hard: number;
  };
  nextCard: any;
  priority: string;
};

interface DebugPanelProps {
  onLog?: (message: string, data?: any) => void;
}

export default function DebugPanel({ onLog }: DebugPanelProps) {
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [visible, setVisible] = useState(true);
  const [algorithmState, setAlgorithmState] = useState<AlgorithmState | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const baseId = useId();
  const logCounterRef = useRef(0);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  const addLog = (message: string, data?: any) => {
    logCounterRef.current += 1;
    const newLog: DebugLog = {
      id: `${baseId}-${logCounterRef.current}`,
      timestamp: new Date().toLocaleTimeString(),
      message,
      data,
    };

    setLogs(prev => [...prev.slice(-49), newLog]); // Keep last 50 logs

    if (onLog) {
      onLog(message, data);
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const loadAlgorithmState = async () => {
    try {
      addLog('🔄 Loading algorithm state...');
      const response = await apiClient.getAllCards();

      const now = new Date().getTime();
      const buckets = {
        new: [] as any[],
        expired: [] as any[],
        waiting: [] as any[],
        mastered: [] as any[],
        hard: [] as any[],
      };

      response.cards.forEach(card => {
        if (card.is_hard || card.incorrect_count >= 10) {
          buckets.hard.push(card);
        } else if (card.current_bin === 11) {
          buckets.mastered.push(card);
        } else if (card.current_bin === 0) {
          buckets.new.push(card);
        } else if (card.next_review_at && new Date(card.next_review_at).getTime() <= now) {
          buckets.expired.push(card);
        } else {
          buckets.waiting.push(card);
        }
      });

      // Sort expired by priority (higher bin first, then older time)
      buckets.expired.sort((a, b) => {
        if (a.current_bin !== b.current_bin) {
          return b.current_bin - a.current_bin;
        }
        return new Date(a.next_review_at).getTime() - new Date(b.next_review_at).getTime();
      });

      const state: AlgorithmState = {
        buckets: {
          expired: buckets.expired.length,
          new: buckets.new.length,
          waiting: buckets.waiting.length,
          mastered: buckets.mastered.length,
          hard: buckets.hard.length,
        },
        nextCard: buckets.expired[0] || buckets.new[0] || null,
        priority:
          buckets.expired.length > 0 ? 'EXPIRED' : buckets.new.length > 0 ? 'NEW' : 'WAITING',
      };

      setAlgorithmState(state);
      addLog('📊 Algorithm state updated', state);
    } catch (error) {
      addLog(
        '❌ Error loading algorithm state',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  // Expose addLog function to parent components
  useEffect(() => {
    (window as any).debugLog = addLog;
    return () => {
      delete (window as any).debugLog;
    };
  }, []);

  if (!visible) {
    return (
      <button
        onClick={() => setVisible(true)}
        className='fixed bottom-4 right-4 bg-gray-800/90 text-green-400 px-2 py-1 text-xs rounded z-50 font-mono'
      >
        Show Debug
      </button>
    );
  }

  return (
    <div className='fixed w-full inset-x-0 bottom-0 bg-gray-900/90 text-green-400 border-2 border-gray-700 p-3 font-mono text-xs max-h-96 overflow-hidden z-50'>
      {/* Header */}
      <div className='flex justify-between items-center mb-2 text-white'>
        <strong>DEBUG</strong>
        <button
          onClick={() => setVisible(false)}
          className='bg-gray-700 text-white px-2 py-1 rounded text-xs hover:bg-gray-600'
        >
          Hide
        </button>
      </div>

      {/* Algorithm State Summary */}
      {algorithmState && (
        <div className='mb-2 p-2 bg-gray-800 rounded border border-gray-600'>
          <div className='text-cyan-400 mb-1'>📊 Current State:</div>
          <div className='grid grid-cols-2 gap-1 text-xs'>
            <span className='text-red-400'>Expired: {algorithmState.buckets.expired}</span>
            <span className='text-blue-400'>New: {algorithmState.buckets.new}</span>
            <span className='text-yellow-400'>Waiting: {algorithmState.buckets.waiting}</span>
            <span className='text-purple-400'>Mastered: {algorithmState.buckets.mastered}</span>
            <span className='text-gray-400'>Hard: {algorithmState.buckets.hard}</span>
            <span className='text-cyan-400'>Priority: {algorithmState.priority}</span>
          </div>
          {algorithmState.nextCard && (
            <div className='mt-1 text-green-300'>
              Next: "{algorithmState.nextCard.word}" (Bin {algorithmState.nextCard.current_bin})
            </div>
          )}
        </div>
      )}

      {/* Logs */}
      <div className='bg-black/20 border border-gray-700 rounded p-2 max-h-48 overflow-y-auto'>
        {logs.length === 0 ? (
          <div className='text-gray-500'>No logs yet...</div>
        ) : (
          logs.map(log => (
            <div key={log.id} className='mb-2 border-b border-gray-800 pb-1 last:border-b-0'>
              <div>
                <span className='text-gray-400'>[{log.timestamp}]</span>{' '}
                <span className='text-cyan-400'>{log.message}</span>
              </div>
              {log.data && (
                <pre className='text-yellow-300 text-xs mt-1 whitespace-pre-wrap'>
                  {JSON.stringify(log.data, null, 2)}
                </pre>
              )}
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>

      {/* Controls */}
      <div className='flex gap-2 mt-2'>
        <button
          onClick={clearLogs}
          className='bg-gray-700 text-white px-2 py-1 rounded text-xs hover:bg-gray-600'
        >
          Clear
        </button>
        <button
          onClick={loadAlgorithmState}
          className='bg-gray-700 text-white px-2 py-1 rounded text-xs hover:bg-gray-600'
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
